<template>
  <div id="whale-backtester-restrictions-tab">
    <helpers-section-header class="mt-0 mb-2">
      Stock Filters
    </helpers-section-header>
    <p class="text-body-2 mb-6">
      These filters are applied <strong>after</strong> your signals generate buy candidates.
      Any stock that fails a filter is removed before position sizing runs. Use them to enforce
      universe constraints — minimum liquidity, preferred sectors, market cap tiers, security
      types — regardless of what your signals suggest.
    </p>

    <v-container fluid pa-0>
      <v-row density="compact">
        <!-- Filter by stock market cap -->
        <v-col cols="12" xl="6">
          <helpers-section-sub-header class="mb-1">Market Cap Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Only buy stocks in the selected market cap tiers. Leave blank to allow any size.
            Select multiple tiers to define your investable universe — for example, small + mid
            cap only for a SMID strategy.
          </p>
          <v-select
            v-model="whaleTraderCriteria.filters.market_caps"
            :items="marketCaps"
            return-object
            item-title="name"
            multiple
            density="compact"
            variant="outlined"
            chips
            closable-chips
            clearable
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <v-col v-show="$vuetify.display.xl" xl="6" />

        <!-- Optionally filter by Sector/Industry -->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-1">Sector / Industry Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Restrict buys to stocks in the checked sectors and industries. Useful for building
            sector-specific strategies or excluding industries outside your mandate. Leave all
            unchecked to allow any sector.
          </p>
          <v-treeview
            v-model:selected="whaleTraderCriteria.filters.industries"
            :items="sectors"
            open-on-click
            item-value="id"
            transition
            :return-object="false"
            select-strategy="classic"
            hoverable
            item-title="name"
            selectable
            density="compact"
          />
        </v-col>

        <v-col cols="12" xl="6">
          <helpers-section-sub-header class="mb-1">Countries to Exclude</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Exclude stocks domiciled in specific countries. Useful for avoiding higher-risk
            geographies, stocks with delisting risk, or any country outside your investment
            mandate. Leave blank to allow all countries.
          </p>
          <v-select
            v-model="whaleTraderCriteria.filters.countries"
            :items="stock_countries"
            multiple
            density="compact"
            variant="outlined"
            chips
            closable-chips
            clearable
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <v-col cols="12" xl="6">
          <helpers-section-sub-header class="mb-1">Security Type Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Restrict to specific security types. For a clean equity-only strategy, select
            <strong>Equity</strong> and optionally <strong>ADR</strong>. Leave blank to allow
            all security types.
          </p>
          <v-select
            v-model="whaleTraderCriteria.filters.security_codes"
            :items="security_codes"
            return-object
            item-title="name"
            multiple
            density="compact"
            variant="outlined"
            chips
            closable-chips
            clearable
            hide-details
            class="pt-0 d-inline-flex"
          />
        </v-col>

        <v-col v-show="$vuetify.display.xl" xl="6" />

        <!-- ETF Filter -->
        <v-col cols="12" class="mt-4">
          <helpers-section-sub-header>ETF Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            Control whether exchange-traded funds appear as buy candidates.
            <strong>Exclude ETFs</strong> keeps the portfolio in individual stocks only.
            <strong>ETF holdings only</strong> restricts to ETFs — useful for macro or
            sector-rotation strategies.
          </p>
          <v-radio-group
            v-model="whaleTraderCriteria.filters.etf_filter"
            :row="!$vuetify.display.xs"
            hide-details
            class="mt-1"
          >
            <v-radio :value="0" label="Any" color="primary" />
            <v-radio :value="1" label="ETF Holdings only" color="primary" />
            <v-radio :value="2" label="Exclude ETFs" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- ADR Filter -->
        <v-col cols="12">
          <helpers-section-sub-header>ADR Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            ADRs (American Depositary Receipts) are foreign stocks traded on US exchanges.
            Excluding them limits the portfolio to domestic companies. Selecting
            <strong>Foreign ADR only</strong> targets international exposure via US-listed
            foreign names.
          </p>
          <v-radio-group
            v-model="whaleTraderCriteria.filters.adr_filter"
            :row="!$vuetify.display.xs"
            hide-details
            class="mt-1"
          >
            <v-radio :value="0" label="Any" color="primary" />
            <v-radio :value="1" label="Foreign ADR only" color="primary" />
            <v-radio :value="2" label="Exclude ADRs" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- CEF Filter -->
        <v-col cols="12" class="mt-4">
          <helpers-section-sub-header>Closed-End Fund (CEF) Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            Closed-end funds trade like stocks but hold a fixed pool of assets and often trade
            at a discount to NAV. Most equity strategies will want to exclude them. Selecting
            <strong>CEF holdings only</strong> is useful for income-focused models targeting
            discounted CEF portfolios.
          </p>
          <v-radio-group
            v-model="whaleTraderCriteria.filters.cef_filter"
            :row="!$vuetify.display.xs"
            hide-details
            class="mt-1"
          >
            <v-radio :value="0" label="Any" color="primary" />
            <v-radio :value="1" label="CEF Holdings only" color="primary" />
            <v-radio :value="2" label="Exclude CEFs" color="primary" />
          </v-radio-group>
        </v-col>

        <!-- Avg. Stock Price Restrictions -->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-1">Average Stock Price Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Filter by the stock's 90-day average closing price. Set a minimum (e.g., <strong>$5</strong>)
            to exclude penny stocks, which typically have poor liquidity and higher manipulation
            risk. A maximum can exclude very high-priced shares that would skew position sizing.
            Leave blank for no price restriction.
          </p>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_stock_price_min"
                type="number"
                label="Min price (90-day avg)"
                hide-details
                density="compact"
                variant="outlined"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_stock_price_max"
                type="number"
                label="Max price (90-day avg)"
                hide-details
                density="compact"
                variant="outlined"
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6" xl="8" class="pa-0" />

        <!-- NATR Restrictions -->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-1">Normalized ATR (Volatility) Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            NATR (Normalized Average True Range) is ATR expressed as a percentage of price —
            a volatility measure comparable across stocks at any price level. A 28-day NATR of
            2% means the stock moves roughly 2% per day on average. Set a minimum to filter out
            slow-moving stocks, or a maximum to exclude highly volatile names your position
            sizing model may not handle safely. Leave blank for no volatility restriction.
          </p>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.natr_min"
                type="number"
                density="compact"
                variant="outlined"
                label="Min NATR (28-day avg)"
                hide-details
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.natr_max"
                type="number"
                density="compact"
                variant="outlined"
                label="Max NATR (28-day avg)"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6" xl="8" class="pa-0" />
        <!-- Dividend Yield -->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-1">Dividend Yield Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Require a minimum annual dividend yield. Useful for income-focused strategies or
            for filtering out non-dividend payers. Leave blank to allow any yield including zero.
          </p>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="4">
              <v-text-field
                v-model="whaleTraderCriteria.filters.dividend_yield_min"
                type="number"
                density="compact"
                variant="outlined"
                label="Min. Dividend Yield"
                hide-details
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="9" />
          </v-row>
        </v-col>
        <v-col cols="12" md="8" xl="8" class="pa-0" />
        <!-- insider ownership-->
        <v-col cols="12" md="6" xl="4">
          <helpers-section-sub-header class="mb-1">Insider Ownership Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Filter by the percentage of shares held by company insiders. High insider ownership
            (e.g., 10%+) can indicate management conviction and alignment with shareholders.
            A maximum can exclude founder-controlled companies where the public float is very
            limited. Leave blank for no insider ownership restriction.
          </p>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.insider_ownership_min"
                type="number"
                label="Min insider ownership %"
                hide-details
                density="compact"
                variant="outlined"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.insider_ownership_max"
                type="number"
                label="Max insider ownership %"
                density="compact"
                variant="outlined"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12" md="6" xl="8" class="pa-0" />

        <!-- Avg. Daily Volume -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-1">Average Daily Share Volume</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Filter by the average number of shares traded per day. A minimum prevents buying
            illiquid stocks where large positions are difficult to enter and exit cleanly.
            A typical minimum for small/mid-cap strategies is 100,000–500,000 shares per day.
            Leave blank for no volume restriction.
          </p>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_daily_volume_min"
                type="number"
                label="Min daily shares"
                hide-details
                density="compact"
                variant="outlined"
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_daily_volume_max"
                type="number"
                label="Max daily shares"
                density="compact"
                variant="outlined"
                hide-details
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- Avg. Dollar Volume Liquidity -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header class="mb-1">Average Daily Dollar Volume</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Filter by average daily trading value (price × shares). More meaningful than share
            volume alone because it accounts for stock price — a $5 stock trading 1M shares/day
            has the same dollar volume as a $50 stock trading 100K shares/day. A minimum of
            <strong>$5M–$10M/day</strong> is a common liquidity floor for institutional-style
            strategies. Leave blank for no dollar volume restriction.
          </p>
          <v-row>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_dollar_vol_liquidity_min"
                type="number"
                label="Min avg daily $ volume"
                density="compact"
                variant="outlined"
                hint="e.g. 5000000 for $5M/day minimum"
                persistent-hint
                class="pt-0"
              />
            </v-col>
            <v-col :shrink="!$vuetify.display.xs" cols="6">
              <v-text-field
                v-model="whaleTraderCriteria.filters.avg_dollar_vol_liquidity_max"
                type="number"
                label="Max avg daily $ volume"
                density="compact"
                variant="outlined"
                hint="Leave blank for no upper limit"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <v-col cols="12">
          <helpers-section-sub-header class="mb-1">Volatility vs S&amp;P Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Only allow stocks whose historical volatility is a certain percentage above or below
            the S&amp;P 500's volatility over the chosen time period. Use <strong>&gt;</strong> to
            target higher-beta stocks, or <strong>&lt;</strong> to restrict to lower-volatility
            names relative to the benchmark.
          </p>
        </v-col>
        <v-col cols="12">
          <v-checkbox
            v-model="whaleTraderCriteria.filters.volatility_enabled"
            label="Enabled"
            hide-details
            density="compact"
            variant="outlined"
            class="shrink ma-0"
          />
        </v-col>
        <v-col cols="12">
          <v-row density="compact">
            <v-col cols="12" md="4" lg="3">
              <v-select
                v-model="whaleTraderCriteria.filters.volatility_time_period_id"
                :items="volatility_time_periods"
                density="compact"
                item-title="text"
                variant="outlined"
                :disabled="!whaleTraderCriteria.filters.volatility_enabled"
                label="Time Period Volatility"
              />
            </v-col>
            <v-col cols="12" md="4" lg="3">
              <v-select
                v-model="whaleTraderCriteria.filters.volatility_direction"
                :disabled="!whaleTraderCriteria.filters.volatility_enabled"
                :items="['>', '<']"
                density="compact"
                variant="outlined"
                label="greater than or less than the S&amp;P;"
                hint="Direction (greater than or less than) S&amp;P"
              />
            </v-col>
            <v-col cols="12" md="4" lg="3">
              <v-text-field
                v-model="whaleTraderCriteria.filters.volatility_percentage"
                :disabled="!whaleTraderCriteria.filters.volatility_enabled"
                label="% difference"
                density="compact"
                variant="outlined"
                hint="% difference between stock volatility and S&amp;P volatility"
                persistent-hint
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Limit same issuer -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header>One Security per Issuer</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Prevents holding multiple share classes from the same company simultaneously
            (e.g., won't buy GOOGL if GOOG is already held). Avoids unintentional double
            exposure to a single underlying business.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-checkbox
                v-model="whaleTraderCriteria.filters.limit_issuers"
                hide-details
                density="compact"
                variant="outlined"
                label="Limit to one security per issuer"
                class="shrink mr-2"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- Limit new purchase of a previously held stock -->
        <v-col v-if="!whaleTraderCriteria.general.screener" cols="12" mb-2>
          <helpers-section-sub-header>Re-Buy Cooldown Period</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            After selling a stock, prevent the backtester from re-buying it for this many days.
            Avoids immediately re-entering positions that were just exited — which can cause
            whipsaw losses when a stock is in a choppy range. Leave blank to allow immediate
            re-entry.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="whaleTraderCriteria.filters.limit_new_purchases_days"
                type="number"
                suffix="days"
                density="compact"
                variant="outlined"
                label="Cooldown days after selling"
                hint="Leave blank to allow immediate re-entry"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>
        <!-- minimum history -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header>Minimum Trading History</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            Require a stock to have been publicly traded for at least this many days before it
            can be purchased. A value of <strong>252+</strong> days (one trading year) filters
            out recent IPOs that lack sufficient price history for technical analysis and
            volatility calculations.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="whaleTraderCriteria.filters.minimum_trading_history_days"
                type="number"
                suffix="days"
                density="compact"
                variant="outlined"
                label="Minimum trading days required"
                hint="Leave blank to allow any stock including recent IPOs"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <!-- maximum history -->
        <v-col cols="12" mb-2>
          <helpers-section-sub-header>Maximum Trading History</helpers-section-sub-header>
          <p class="text-body-2 mb-1">
            Optionally limit buys to stocks that have been publicly traded for fewer than this
            many days — useful for strategies specifically targeting newer listings or recent
            spin-offs. Leave blank to allow any stock regardless of listing age.
          </p>
          <v-row>
            <v-col cols="12" sm="6" md="4" xl="3">
              <v-text-field
                v-model="whaleTraderCriteria.filters.maximum_trading_history_days"
                type="number"
                suffix="days"
                density="compact"
                variant="outlined"
                label="Maximum trading days allowed"
                hint="Leave blank for no upper limit on trading history"
                persistent-hint
                class="pt-0"
              />
            </v-col>
          </v-row>
        </v-col>

        <v-col cols="12">
          <helpers-section-sub-header class="mb-1">52-Week High / Low Proximity Filter</helpers-section-sub-header>
          <p class="text-body-2 mb-2">
            Filter for stocks trading close to their 52-week high or low, measured in ATR units
            (using the prior 2-week Average True Range). Setting <strong>1 ATR within the
            52-week high</strong> selects stocks near breakout territory — a momentum filter.
            Setting <strong>1 ATR within the 52-week low</strong> targets deeply oversold
            candidates — a mean-reversion filter. Enable the checkbox next to each to activate
            that filter.
          </p>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <v-row no-gutters>
            <v-checkbox
              v-model="whaleTraderCriteria.filters.within_atr_high_enabled"
              hide-details
              density="compact"
              variant="outlined"
              class="shrink mr-2"
            />
            <v-text-field
              v-model="whaleTraderCriteria.filters.within_atr_high"
              :disabled="!whaleTraderCriteria.filters.within_atr_high_enabled"
              type="number"
              density="compact"
              variant="outlined"
              label="#ATR within 52-week high"
              hide-details
            />
          </v-row>
        </v-col>
        <v-col cols="12" sm="6" lg="3">
          <v-row no-gutters>
            <v-checkbox
              v-model="whaleTraderCriteria.filters.within_atr_low_enabled"
              hide-details
              density="compact"
              variant="outlined"
              class="shrink mr-2"
            />
            <v-text-field
              v-model="whaleTraderCriteria.filters.within_atr_low"
              :disabled="!whaleTraderCriteria.filters.within_atr_low_enabled"
              type="number"
              density="compact"
              variant="outlined"
              label="#ATR within 52-week low"
              persistent-hint
              hide-details
            />
          </v-row>
        </v-col>
      </v-row>
      <v-row>
        <!-- Optionally restrict to specific securities -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header class="mb-2">
            Restrict to Specific Securities (Optional)
          </helpers-section-sub-header>
          <p class="text-body-2 mb-4">
            Only buy stocks from this list, regardless of what your signals generate. Any stock
            not on this list will be ignored even if it passes all other filters. Useful for
            building a fixed-universe strategy. Leave empty to allow any stock.
          </p>

          <core-stock-manager
            :key="'only-stock-manager-' + current_trading_model.id"
            v-model:stock-group="whaleTraderCriteria.filters.included_stocks"
            flat
            no-padding
          />
        </v-col>

        <!-- Optionally exclude specific securities -->
        <v-col cols="12" mb-6>
          <helpers-section-sub-header class="mb-2">
            Exclude Specific Securities (Optional)
          </helpers-section-sub-header>
          <p class="text-body-2 mb-4">
            Always skip these specific stocks, even if they appear as valid buy candidates from
            your signals. Useful for ethical screens, known problem tickers, or stocks you
            already hold elsewhere. Leave empty to exclude nothing.
          </p>

          <core-stock-manager
            :key="'exc-stock-manager-' + current_trading_model.id"
            v-model:stock-group="whaleTraderCriteria.filters.excluded_stocks"
            flat
            no-padding
          />
        </v-col>
      </v-row>
    </v-container>
  </div>
</template>

<script>
// Modules
import { mapWritableState } from 'pinia'
import { useUserStore, useAppStore, useWwStore } from '@/store'
import AccessMixin from '@/mixins/access'

export default {
  mixins: [AccessMixin],
  data() {
    return {
      marketCaps: [
        { name: 'Over $200 billion', id: 1 },
        { name: '$100 billion - $200 billion', id: 2 },
        { name: '$50 billion - $100 billion', id: 3 },
        { name: '$20 billion - $50 billion', id: 4 },
        { name: '$10 billion - $20 billion', id: 5 },
        { name: '$2 billion - $10 billion', id: 6 },
        { name: '$1 billion - $2 billion', id: 7 },
        { name: '$500 million - $1 billion', id: 8 },
        { name: '$250 million - $500 million', id: 9 },
        { name: 'Below $250 million', id: 10 }
      ],
      security_codes: [
        { name: 'Equity', code: 'EQS' },
        { name: 'ETF', code: 'ETF' },
        { name: 'Unit', code: 'UNT' },
        { name: 'ADR', code: 'DR' },
        { name: 'Closed-End-Fund', code: 'CEF' },
        { name: 'SP ETN REDEEM', code: 'SP' },
        { name: 'PFS Perpetual', code: 'PFS' },
        { name: 'PRF Perpetual', code: 'PRF' },
        { name: 'Systemic Transfer Plan (STP)', code: 'STP' },
        { name: 'Warrant', code: 'WAR' },
        { name: 'Tradeable Rights', code: 'TRT' },
        { name: 'Bond', code: 'BND' },
        { name: 'Contingent Value Right (CVR)', code: 'CVR' },
        { name: 'When Issued (WI)', code: 'WIS' },
        { name: 'Unknown', code: null },
        { name: 'Redeemable Shares', code: 'RDS' },
        { name: 'Chess Depository Interest', code: 'CDI' },
        { name: 'Exchange Traded Commodity (ETC)', code: 'ETC' },
        //{name:'Mutual Fund', code:'MF'},
        { name: 'Letter of Allotment', code: 'LOA' },
        { name: 'Income Fund', code: 'IF' },
        { name: 'Redemption Rights', code: 'RDR' },
        { name: 'Subscription Receipts', code: 'SRT' }
      ]
    }
  },
  computed: {
    ...mapWritableState(useAppStore, [
      'whaleTraderCriteria',
      'current_trading_model',
      'signalView',
      'volatility_time_periods'
    ]),
    ...mapWritableState(useWwStore, ['sectors', 'stock_countries'])
    // ...mapWritableState(useAppStore, {
    //   criteria: store => store.whaleTraderCriteria.filters,
    /*criteria(store) {
        return store.whaleTraderCriteria.filters
      },*/

    // })
    // ...mapWritableState(useAppStore, {criteria: 'whaleTraderCriteria.filters'}),
  }
}
</script>
